import { TunnuksenKieli } from "./KieliValinta";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTaulukko } from "./taulukko";
import { nimeamattomanSana, nimeamattomienSanat } from "./sanat";
import { luku } from "./luvut";
import {
  api, ApiError,
  type Kayttaja, type Osaamisluokka, type Resurssi, type TesListaus,
  type Patevyystyyppi, type Kohde,
} from "./api";
import { TyomaaValinta } from "./TyomaaValinta";
import Liitteet from "./Liitteet";
import HenkilonPatevyydet from "./HenkilonPatevyydet";
import HenkilonPoissaolot from "./HenkilonPoissaolot";
import { Salaisuus } from "./Salaisuus";
import { TILAVARI } from "./Tila";
import PohjaTuontiPaneeli from "./PohjaTuonti";

import { Ohje } from "./Ohje";
import { t as tr, virheTeksti, lokaali } from "./kieli";
import { nayttonimi } from "./nayttonimi";
/* ═══════════════════════════════════════════════════════════
   Henkilöt ja osaamiset.

   Kaksi asiaa, joita ennen ei voinut tehdä muuten kuin kannassa:
   osaamisnimikkeiden lisääminen ja työaikojen muuttaminen.
   ═══════════════════════════════════════════════════════════ */

/** Lajitteluavaimet. Nimi on oletus; muut ovat lukuja joita
 *  työnjohto vertaa toisiinsa. `Työpäivät` jää lajittelematta:
 *  viikonpäivien joukolle ei ole mielekästä järjestystä. */
type LajitteluAvain = "nimi" | "osaamiset" | "tyopaiva" | "max_pv" | "max_vko" | "max_kk";

export default function Henkilot({ kayttaja, osoiteHaku, asetaOsoiteHaku }: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));
  // Oikeuskartoitus 16.10.2026 kohta 6: pätevyyksien kirjaus henkilön alla samalla ehdolla kuin Pätevyydet-näkymässä ja
  // API:ssa (pk, su tai oikeus hitsauskoordinointi) — EI saaMuokata, joka avaa henkilön kaikki kentät.
  const saaMuokataPatevyyksia = saaMuokata || kayttaja.oikeudet.includes("hitsauskoordinointi");
  // 246: pätevyyksien luku (sama ehto kuin api/src/auth.ts saaPatevyydet).
  const saaNahdaPatevyydet = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r))
    || kayttaja.oikeudet.includes("hitsauskoordinointi") || kayttaja.oikeudet.includes("patevyydet_luku");
  const onPaakayttaja = kayttaja.roolit.includes("paakayttaja");

  const [henkilot, setHenkilot] = useState<Resurssi[]>([]);
  const [luokat, setLuokat] = useState<Osaamisluokka[]>([]);
  const [tesit, setTesit] = useState<TesListaus[]>([]);
  const [patevyystyypit, setPatevyystyypit] = useState<Patevyystyyppi[] | null>(null);
  const [avattu, setAvattu] = useState<number | null>(null);
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lisataan, setLisataan] = useState(false);

  const lataa = useCallback(async () => {
    setLadataan(true);
    setVirhe(null);
    try {
      const [h, o] = await Promise.all([api.resurssit(), api.osaamisluokat()]);
      setHenkilot(h);
      setLuokat(o);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.haku_virhe"));
    } finally { setLadataan(false); }
    // TES-lista erikseen: se vaatii tarjous-moduulin, eikä sen puute saa
    // kaataa koko henkilönäkymää. Ilman moduulia palkkakenttä näkyy silti.
    try { setTesit(await api.tesitLista()); } catch { setTesit([]); }
    // Pätevyystyypit: null = moduuli pois → pätevyysosiota ei näytetä.
    try { setPatevyystyypit(await api.patevyystyypit()); } catch { setPatevyystyypit(null); }
  }, []);


  useEffect(() => { void lataa(); }, [lataa]);

  // Hookit ennen ehdollista palautusta (rules-of-hooks): arvo() ja taulukko ovat tässä
  // eikä lähempänä käyttökohtaansa samasta syystä kuin Hitsausloki.tsx:n osoite-efekti.
  const arvo = (h: typeof henkilot[number], a: LajitteluAvain): string | number => {
    switch (a) {
      case "nimi":      return `${h.sukunimi} ${h.etunimi}`.toLowerCase();
      case "osaamiset": return (h.osaamiset ?? []).length;
      // Nämä kaksi eivät ole nullable, joten oletusarvo olisi kuollut
      // haara joka kirjaa väitteen: "tyhjä työpäivä on nolla". Se ei
      // ole totta — tyhjä olisi *ei tiedetä*, ja se on eri asia kuin
      // mitattu nolla. Väitettä ei jätetä odottamaan päivää jona
      // kenttä muuttuu nullableksi.
      case "tyopaiva":  return h.tunnit_per_paiva;
      case "max_pv":    return h.maksimi_per_paiva;
      // Nämä kaksi OVAT nullable, ja tyhjä tarkoittaa ETTEI KATTOA OLE
      // (näkymän alatunniste sanoo sen). Ei katto on suurin mahdollinen,
      // ei pienin — sama sääntö eri suunnasta kuin yllä.
      case "max_vko":   return h.maksimi_per_viikko   ?? Number.POSITIVE_INFINITY;
      case "max_kk":    return h.maksimi_per_kuukausi ?? Number.POSITIVE_INFINITY;
    }
  };
  // A3–A7 (erä 22): lajittelu ja haku ovat jaettu mekanismi (taulukko.tsx), joka
  // nostettiin tästä näkymästä. Avaimet ja niiden säännöt (tyhjä katto = ääretön)
  // ovat yllä arvo():ssa; toissijainen avain on aina nimi (sukunimi ensin).
  const AVAIMET: LajitteluAvain[] = ["nimi", "osaamiset", "tyopaiva", "max_pv", "max_vko", "max_kk"];
  const t = useTaulukko(henkilot, {
    avaimet: Object.fromEntries(AVAIMET.map((a) => [a, (h: typeof henkilot[number]) => arvo(h, a)])) as Record<LajitteluAvain, (h: typeof henkilot[number]) => string | number>,
    hakukentat: (h) => [h.nimi, h.lyhenne, h.nimike, ...(h.osaamiset ?? [])],
    oletus: "nimi", yksikko: tr("hen.yksikko"), osoiteHaku, asetaOsoiteHaku,
  });
  const otsikko = t.Otsikko;
  const omat = t.rivit.filter((h) => !h.nimeamaton);
  const vuokra = t.rivit.filter((h) => h.nimeamaton);

  if (virhe) {
    return (
      <div className="tyhja">
        <p>{virhe}</p>
        <button className="nappi nappi--paa" onClick={() => void lataa()}>{tr("hen.yrita_uudelleen")}</button>
      </div>
    );
  }

  /*
   * Haku nimellä JA nimikkeellä.
   *
   * 31 henkilöä mahtuu ruudulle, mutta nimike on se jolla heitä
   * etsitään: "kuka on hitsaaja" on kysymys jota kysytään useammin
   * kuin "missä on Virtanen". Yhdellä voi olla useampi nimike, joten
   * osuma etsitään koko listasta eikä ensimmäisestä.
   *
   * Suodatus on selaimessa, koska lista on jo kokonaan ladattu.
   * Palvelinhaku olisi verkkokutsu jokaisesta näppäimenpainalluksesta
   * eikä toisi mitään mitä tässä ei jo ole.
   */

  /* Sarakelajittelu. Oletus on sukunimi — ei "ei mitään", koska
     lajittelematon lista on vain edellinen lajittelu ilman nimeä.

     Tyhjä maksimi tarkoittaa ETTEI KATTOA OLE (ks. alatunniste), joten
     se lajittuu suurimmaksi eikä pienimmäksi. Nolla ja tyhjä ovat eri
     asioita täälläkin. */
  // Sana luetaan tyosuhteesta, ei kovakoodata. Ks. Kapasiteetti.tsx
  // nimeamattomanSana() — pekilon 41 paikkaa ovat `alihankinta`, ja
  // "41 vuokrapaikkaa" oli yksinkertaisesti vaara lause.

  return (
    <>
      <header className="head">
        <div className="eyebrow">{tr("hen.eyebrow")}</div>
        <div className="head__rivi">
          <h1>{tr("hen.otsikko")}</h1>
          <div style={{ display: "flex", gap: 8 }}>
            {t.Haku({ placeholder: tr("hen.haku") })}
            <a className="nappi nappi--kevyt" href={api.hitsaajalistaVienti()}>{tr("hen.vie_hitsaajalista")}</a>
            {saaMuokata && !lisataan && (
              <button className="nappi nappi--paa" onClick={() => setLisataan(true)}>
                {tr("hen.lisaa_henkilo")}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Ohje ylös (Toni 23.9.2026): kuka saa tehdä mitä, ja mitä luvut tarkoittavat. */}
      <Ohje
        kuka={<>{tr("hen.ohje.kuka")}</>}
        luvut={<>{tr("hen.ohje.luvut")}</>} />

      {saaMuokata && (
        <PohjaTuontiPaneeli
          otsikko={tr("hen.tuonti_otsikko")}
          selite={tr("hen.tuonti_selite")}
          pohjaUrl={api.henkilopohjaUrl()}
          tuo={(sisalto, tiedosto, vahvista, paivita) => api.tuoHenkilot(sisalto, tiedosto, vahvista, paivita)}
          paivitysTeksti={tr("hen.tuonti_paivitys")}
          valmis={() => void lataa()} />
      )}

      {lisataan && (
        <UusiHenkilo
          luokat={luokat}
          peruuta={() => setLisataan(false)}
          valmis={async () => { setLisataan(false); await lataa(); }}
        />
      )}

      <Osaamiset luokat={luokat} saaMuokata={saaMuokata} onPaakayttaja={onPaakayttaja} paivita={lataa} />

      {ladataan && <div className="lataa lataa--ohut">{tr("ltar.ladataan")}</div>}

      <section className="panel scroll">
        <div className="panel__head">
          <h2>{tr("hen.henkilosto")}</h2>
          <span className="hint">{tr("hen.omaa", { n: omat.length })}{nimeamattomienSanat(vuokra) ? ` · ${nimeamattomienSanat(vuokra)}` : ""}</span>
        </div>
        <table className="tbl tbl--henkilot">
          <thead>
            <tr>
              {/* Lajiteltavat otsikot ovat painikkeita, ja nuoli kertoo
                  suunnan. `Työpäivät` ei ole lajiteltava: viikonpäivien
                  joukolle ei ole mielekästä järjestystä. */}
              <th className="th--nimi">{otsikko("nimi", tr("tk.henkilo"))}</th>
              <th className="th--vasen">{otsikko("osaamiset", tr("hen.osaamiset"))}</th>
              <th>{tr("hen.tyopaivat")}</th>
              <th>{otsikko("tyopaiva", tr("hen.tyopaiva"))}</th>
              <th>{otsikko("max_pv", tr("hen.max_pv"))}</th>
              <th>{otsikko("max_vko", tr("hen.max_vko"))}</th>
              <th>{otsikko("max_kk", tr("hen.max_kk"))}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {omat.map((h) => (
              <HenkiloRivi
                key={h.id} henkilo={h} luokat={luokat} tesit={tesit}
                patevyystyypit={patevyystyypit} saaMuokata={saaMuokata}
                paakayttaja={kayttaja.roolit.includes("paakayttaja")}
                saaNollataPin={kayttaja.roolit.some((r) => r === "paakayttaja" || r === "tyonjohtaja")}
                saaArkaluonteiset={kayttaja.oikeudet.includes("arkaluonteiset_henkilotiedot")}
                saaNahdaPatevyydet={saaNahdaPatevyydet} saaMuokataPatevyyksia={saaMuokataPatevyyksia}
                auki={avattu === h.id}
                avaa={() => setAvattu(avattu === h.id ? null : h.id)}
                paivita={lataa}
              />
            ))}
            {/* Vuokrapaikat omaksi ryhmakseen. VA1-VA5 eivat ole
                henkiloita vaan nimeamattomia paikkoja, ja ilman
                valiotsikkoa ne nayttivat ihmisilta joilta puuttuu
                nimi. Ne olivat jo listan lopussa (API lajittelee
                nimeamaton ensin) - vain raja puuttui. */}
            {vuokra.length > 0 && (
              <tr className="ryhmaotsikko">
                <th colSpan={8}>
                  {tr("hen.vuokrapaikat")}
                  <span className="dim"> · {tr("hen.vuokrapaikat_selite")}</span>
                </th>
              </tr>
            )}
            {vuokra.map((h) => (
              <HenkiloRivi
                key={h.id} henkilo={h} luokat={luokat} tesit={tesit}
                patevyystyypit={patevyystyypit} saaMuokata={saaMuokata}
                paakayttaja={kayttaja.roolit.includes("paakayttaja")}
                saaNollataPin={kayttaja.roolit.some((r) => r === "paakayttaja" || r === "tyonjohtaja")}
                saaArkaluonteiset={kayttaja.oikeudet.includes("arkaluonteiset_henkilotiedot")}
                saaNahdaPatevyydet={saaNahdaPatevyydet} saaMuokataPatevyyksia={saaMuokataPatevyyksia}
                auki={avattu === h.id}
                avaa={() => setAvattu(avattu === h.id ? null : h.id)}
                paivita={lataa}
              />
            ))}
          </tbody>
        </table>
      </section>

    </>
  );
}

/* ── osaamisnimikkeet ───────────────────────────────────── */

/** Hitsaustyön myönnön perusteet (129). Sama lista kuin migraation 129 rajoite; oletus_127 vain täytöstä. */
const HITSAUSTYON_PERUSTEET: { laji: string; nimi: string }[] = [
  { laji: "hitsaa", get nimi() { return tr("hen.peruste.hitsaa"); } },
  { laji: "hitsausparin_asentaja", get nimi() { return tr("hen.peruste.fitter"); } },
  { laji: "valvoo_hitsausta", get nimi() { return tr("hen.peruste.valvoo_hitsausta"); } },
  { laji: "kirjaa_tai_tarkastaa", get nimi() { return tr("hen.peruste.kirjaa_tarkastaa"); } },
  { laji: "muu", get nimi() { return tr("hen.peruste.muu"); } },
];
/**
 * Peruutuksen perusteet (132). Sammutus sulkee hitsauslokin kaikilta tämän työlajin
 * kenttätekijöiltä, ja ilman perustetta kokeilu, päätös ja vahinko näyttävät riviltä samalta.
 */
const PERUUTUKSEN_PERUSTEET: { laji: string; nimi: string }[] = [
  { laji: "ei_hitsaustyota", get nimi() { return tr("hen.peruste.ei_hitsaustyota"); } },
  { laji: "kirjataan_muualla", get nimi() { return tr("hen.peruste.hitsit_muualla"); } },
  { laji: "myonto_oli_virhe", get nimi() { return tr("hen.peruste.myonto_virhe"); } },
  { laji: "muu", get nimi() { return tr("hen.peruste.muu"); } },
];
/**
 * Laiteasennustyön perusteet (134, Toni 18.9.2026). Sama rakenne kuin hitsaustyöllä,
 * eri sanat: rasti kertoo kuka näkee laiteasennuksen, ei kuka asentaa.
 */
const LAITEASENNUKSEN_PERUSTEET: { laji: string; nimi: string }[] = [
  { laji: "asentaa_laitteita", get nimi() { return tr("hen.peruste.asentaa_laitteita"); } },
  { laji: "valvoo_asennusta", get nimi() { return tr("hen.peruste.valvoo_laiteasennusta"); } },
  { laji: "kuittaa_vaiheita", get nimi() { return tr("hen.peruste.kuittaa_vaiheita"); } },
  { laji: "kayttoonotto", get nimi() { return tr("hen.peruste.kayttoonotto"); } },
  { laji: "muu", get nimi() { return tr("hen.peruste.muu"); } },
];
/** IV-asennustyön perusteet (191, päätös 52). Sama rakenne; rasti kertoo kuka näkee IV-asennuksen ja saa kirjata mittauksia. */
const IV_ASENNUKSEN_PERUSTEET: { laji: string; nimi: string }[] = [
  { laji: "asentaa_kanavistoa", get nimi() { return tr("hen.peruste.asentaa_kanavistoa"); } },
  { laji: "mittaa_tiiviyden_tai_ilmavirran", get nimi() { return tr("hen.peruste.mittaa"); } },
  { laji: "valvoo_asennusta", get nimi() { return tr("hen.peruste.valvoo_iv"); } },
  { laji: "kuittaa_vaiheita", get nimi() { return tr("hen.peruste.kuittaa_suojaus"); } },
  { laji: "muu", get nimi() { return tr("hen.peruste.muu"); } },
];
const IV_ASENNUKSEN_PERUUTUS: { laji: string; nimi: string }[] = [
  { laji: "ei_iv_asennusta", get nimi() { return tr("hen.peruste.ei_iv"); } },
  { laji: "kirjataan_muualla", get nimi() { return tr("hen.peruste.iv_muualla"); } },
  { laji: "myonto_oli_virhe", get nimi() { return tr("hen.peruste.myonto_virhe"); } },
  { laji: "muu", get nimi() { return tr("hen.peruste.muu"); } },
];
const LAITEASENNUKSEN_PERUUTUS: { laji: string; nimi: string }[] = [
  { laji: "ei_laiteasennusta", get nimi() { return tr("hen.peruste.ei_laiteasennusta"); } },
  { laji: "kirjataan_muualla", get nimi() { return tr("hen.peruste.laite_muualla"); } },
  { laji: "myonto_oli_virhe", get nimi() { return tr("hen.peruste.myonto_virhe"); } },
  { laji: "muu", get nimi() { return tr("hen.peruste.muu"); } },
];
const perusteenNimi = (laji: string | null) =>
  laji === "oletus_127" ? tr("hen.peruste.oletus_127")
    : [...HITSAUSTYON_PERUSTEET, ...PERUUTUKSEN_PERUSTEET,
       ...LAITEASENNUKSEN_PERUSTEET, ...LAITEASENNUKSEN_PERUUTUS, ...IV_ASENNUKSEN_PERUSTEET, ...IV_ASENNUKSEN_PERUUTUS].find((p) => p.laji === laji)?.nimi ?? "—";

function Osaamiset({
  luokat, saaMuokata, onPaakayttaja, paivita,
}: {
  luokat: Osaamisluokka[]; saaMuokata: boolean; onPaakayttaja: boolean;
  paivita: () => Promise<void>;
}) {
  const [nimi, setNimi] = useState("");
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  async function lisaa(e: React.FormEvent) {
    e.preventDefault();
    if (!nimi.trim()) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.luoOsaamisluokka(nimi.trim());
      setNimi("");
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  /** Palautus on sama kutsu kuin luonti: sama nimi aktivoi rivin. */
  async function palauta(l: Osaamisluokka) {
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.luoOsaamisluokka(l.nimi, l.jarjestys);
      setIlmoitus(tr("hen.palautettu", { nimi: l.nimi }));
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.palautus_virhe"));
    } finally { setTallennetaan(false); }
  }

  // 129: myöntö kysyy perusteen ennen tallennusta. 132: niin kysyy peruutuskin — sammutus
  // sulkee näkymän kaikilta, eikä perusteeton rivi erota kokeilua päätöksestä jälkikäteen.
  //
  // 23.9.2026 (Toni, kumoaa 22.9.:n «yksi muutos kerrallaan»): useamman ruksin saa muuttaa
  // kerralla, sitten yksi peruste ja yksi «Tallenna muutokset». Peruste tallentuu jokaiselle
  // muutetulle riville samana, muutosloki pysyy rivikohtaisena. Odottava muutos on NETTOMUUTOS
  // tallennettuun tilaan: takaisin käännetty ruksi poistuu odottavista, eikä tyhjä muutos kysy
  // syytä. Perustelista riippuu lipusta ja suunnasta, joten jokaisella muutoslajilla on oma
  // valintansa (yleensä yksi); tarkennus on yhteinen.
  type Lippu = "hitsaustyo" | "laiteasennustyo" | "iv_asennustyo";
  type Muutos = { l: Osaamisluokka; paalle: boolean; lippu: Lippu };
  const [odottavat, setOdottavat] = useState<Muutos[]>([]);
  const [viimeisin, setViimeisin] = useState<number | null>(null);   // rivi jonka alle paneeli avautuu
  const [perusteet, setPerusteet] = useState<Record<string, string>>({});
  const [perusteTeksti, setPerusteTeksti] = useState("");
  const odottava = (l: Osaamisluokka, lippu: Lippu) => odottavat.find((m) => m.l.id === l.id && m.lippu === lippu);
  const ryhma = (m: Pick<Muutos, "lippu" | "paalle">) => `${m.lippu}:${m.paalle ? "paalle" : "pois"}`;
  const ryhmat = [...new Set(odottavat.map(ryhma))];
  const perusteLista = (r: string) => {
    const [lippu, suunta] = r.split(":");
    return lippu === "hitsaustyo"
      ? (suunta === "paalle" ? HITSAUSTYON_PERUSTEET : PERUUTUKSEN_PERUSTEET)
      : lippu === "iv_asennustyo" ? (suunta === "paalle" ? IV_ASENNUKSEN_PERUSTEET : IV_ASENNUKSEN_PERUUTUS)
      : (suunta === "paalle" ? LAITEASENNUKSEN_PERUSTEET : LAITEASENNUKSEN_PERUUTUS);
  };
  const ryhmanOtsikko = (r: string) => {
    const [lippu, suunta] = r.split(":");
    return `${suunta === "paalle" ? tr("hen.myonto") : tr("hen.peruutus")}, ${lippu === "hitsaustyo" ? tr("hen.hitsaustyo") : tr("hen.laiteasennus")}`;
  };
  const perusteValmis = ryhmat.length > 0 && ryhmat.every((r) => perusteet[r])
    && (!ryhmat.some((r) => perusteet[r] === "muu") || !!perusteTeksti.trim());

  function kaanna(l: Osaamisluokka, lippu: Lippu, tallennettu: boolean, uusi: boolean) {
    setIlmoitus(null);
    setOdottavat((v) => {
      const muut = v.filter((m) => !(m.l.id === l.id && m.lippu === lippu));
      return uusi === tallennettu ? muut : [...muut, { l, lippu, paalle: uusi }];
    });
    setViimeisin(l.id);
  }
  function hylkaa() { setOdottavat([]); setPerusteet({}); setPerusteTeksti(""); setViimeisin(null); }

  /** Kumpikin lippu kulkee saman käsittelijän läpi: yksi sääntö, kaksi moduulia. Rivi kerrallaan:
   *  jos yksi kaatuu, jo tallentuneet kerrotaan nimeltä ja kaatunut jää odottamaan. */
  async function tallennaMuutokset() {
    setTallennetaan(true);
    setIlmoitus(null);
    const tehty: string[] = [];
    const jaljella: Muutos[] = [];
    let virhe: string | null = null;
    for (const m of odottavat) {
      if (virhe) { jaljella.push(m); continue; }
      const peruste = { peruste_laji: perusteet[ryhma(m)] ?? "", ...(perusteTeksti.trim() && { peruste: perusteTeksti.trim() }) };
      try {
        if (m.lippu === "hitsaustyo") await api.asetaHitsaustyo(m.l.id, m.paalle, peruste);
        else if (m.lippu === "iv_asennustyo") await api.asetaIvAsennustyo(m.l.id, m.paalle, peruste);
        else await api.asetaLaiteasennustyo(m.l.id, m.paalle, peruste);
        tehty.push(`${m.l.nimi} ${m.lippu === "hitsaustyo" ? tr("hen.hitsaustyo") : m.lippu === "iv_asennustyo" ? tr("hen.iv_asennus") : tr("hen.laiteasennus")} ${m.paalle ? tr("hen.paalle") : tr("hen.pois")}`);
      } catch (e) {
        virhe = `${m.l.nimi}: ${e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.muutos_virhe")}`;
        jaljella.push(m);
      }
    }
    setOdottavat(jaljella);
    if (jaljella.length === 0) { setPerusteet({}); setPerusteTeksti(""); setViimeisin(null); }
    setIlmoitus([tehty.length ? tr("hen.tallennettu_n", { n: tehty.length, lista: tehty.join(" · ") }) : "",
                 virhe ? tr("hen.ei_tallentunut", { virhe }) : ""].filter(Boolean).join(" "));
    await paivita();
    setTallennetaan(false);
  }


  /**
   * Poisto on passivointi — ja se kerrotaan ENNEN klikkausta.
   *
   * Sirussa lukee määrä. Kun nimike katoaa klikkauksesta, ihminen
   * olettaa vieneensä ne henkilöt mukanaan; viesti kertoi ettei niin
   * käynyt, mutta vasta jälkikäteen.
   */
  async function passivoi(l: Osaamisluokka) {
    if (l.henkiloita > 0 &&
        !window.confirm(
          tr("hen.passivoi_vahvistus1", { nimi: l.nimi, n: l.henkiloita }) +
          "\n\n" + tr("hen.passivoi_vahvistus2") +
          "\n\n" + tr("hen.passivoi_vahvistus3"))) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.passivoiOsaamisluokka(l.id);
      setIlmoitus(tr("hen.passivoitu", { nimi: l.nimi }));
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("liite.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{tr("hen.nimikkeet_otsikko")}</h2>
      </div>
      {/* ⛔ Ohje omalle rivilleen otsikon ALLE. `panel__head`in `hint`
          katkesi oikeasta reunasta kohdassa «…ruudukon suo», eikä
          katkennut ohje ole ohje. Toni 22.9.2026. */}
      {/* Teksti on tarkistettu koodia vasten 22.9.2026, ei kirjoitettu muistista:
          vaje ryhmittelee `resurssitarve.osaamisluokka_id`n ja tarjonnan
          `henkilo_osaaminen.osaamisluokka_id`n mukaan (003_kyselyt.sql,
          `f_vaje`) · ruudukon suodin on `r.osaamiset.includes(suodin)`
          (Kapasiteetti.tsx:129) · ruksit koskevat VAIN kenttätunnusta, jonka
          ainoa rooli on kenttätekijä — sekä valikossa (main.tsx, `tyolaji()`
          on `vainKenttatekija`-haarassa) että API:ssa (routes.ts:523).
          Selaintunnus saa näkymänsä roolista, eikä lippu vaikuta siihen. */}
      <div className="hint" style={{ margin: "0 0 10px", maxWidth: "70ch" }}>
        {tr("hen.nimikkeet_ohje")}
        {/* `.app ul` nollaa luettelomerkit koko sovelluksessa (tyylit.css:107);
            tässä ne kuuluvat mukaan, koska kolme kohtaa on lista eikä kappale. */}
        <ul style={{ margin: "4px 0 0", paddingLeft: "1.3em", listStyle: "disc" }}>
          <li><b>{tr("hen.nimikkeet_tarpeet")}</b> {tr("hen.nimikkeet_tarpeet_teksti")}</li>
          <li><b>{tr("hen.nimikkeet_ruudukko")}</b> {tr("hen.nimikkeet_ruudukko_teksti")}</li>
          <li><b>{tr("hen.nimikkeet_nakymat")}</b> {tr("hen.nimikkeet_nakymat_teksti")}</li>
        </ul>
      </div>

      {/* Toni 11.9.2026: tämä lause oli JSX:ssä kommenttina ilman aaltosulkeita, eli
          se piirtyi ruudulle sellaisenaan — asiakkaan näkymään. Sisältö on oikea ja
          kuuluu ihmiselle; kuori oli väärä. Nyt se on selite passivoitujen vieressä. */}
      {/* Kaari: jos taulukko ei mahdu, vieritys tapahtuu TAULUKOSSA eikä
          koko sivulla. Sivun vaakavieritys rikkoo myös yläpalkin ja
          valikon, eikä sitä huomaa työpöydällä lainkaan. */}
      <div className="tbl-kaari">
      <table className="tbl tbl--nimikkeet">
        <thead>
          <tr>
            <th className="th--vasen">{tr("hen.nimike")}</th>
            <th style={{ textAlign: "right" }} title={tr("hen.henkiloita_title")}>
              <span className="kapea-pois">{tr("hen.henkiloita")}</span>
              <span className="kapea-vain">{tr("hen.hlot")}</span>
            </th>
            {/* Pitkä selite kerran otsikon vihjeessä, ei joka rivillä. */}
            <th style={{ textAlign: "center" }}
                title={tr("hen.hitsaustyo_title")}>
              <span className="kapea-pois">{tr("hen.hitsaustyo_sarake")}</span>
              <span className="kapea-vain">{tr("hen.hitsaus_kapea")}</span>
            </th>
            <th style={{ textAlign: "center" }}
                title={tr("hen.laiteasennus_title")}>
              <span className="kapea-pois">{tr("hen.laiteasennus_sarake")}</span>
              <span className="kapea-vain">{tr("hen.laite_kapea")}</span>
            </th>
            <th style={{ textAlign: "center" }}
                title={tr("hen.iv_title")}>
              <span className="kapea-pois">{tr("hen.iv_sarake")}</span>
              <span className="kapea-vain">{tr("hen.iv_kapea")}</span>
            </th>
            {onPaakayttaja && <th></th>}
          </tr>
        </thead>
        <tbody>
          {luokat.map((l) => {
            const poistettu = l.aktiivinen === false;
            return (
            <Fragment key={l.id}>
              <tr className={poistettu ? "dim" : undefined}>
                <th className="th--vasen">
                  {l.nimi}
                  {poistettu && (
                    <span className="hint" style={{ marginLeft: 8 }}
                          title={tr("hen.poistettu_listoilta_title")}>
                      {tr("hen.poistettu_listoilta")}
                    </span>
                  )}
                </th>
                <td style={{ textAlign: "right" }} className="mono">{l.henkiloita}</td>
                {([
                  ["hitsaustyo", !!l.hitsaustyo, l.hitsaustyo_muutos, "hen.nakee_hitsauslokin"],
                  /* Laiteasennuksella EI ole perustetietoa selaimessa (tyyppi
                     tuntee vain `hitsaustyo_muutos`). Tyhjä on tässä rehellinen:
                     vihje kertoo silloin mitä lippu tekee, ei keksittyä perustetta. */
                  ["laiteasennustyo", !!l.laiteasennustyo, undefined, "hen.nakee_laiteasennuksen"],
                  ["iv_asennustyo", !!l.iv_asennustyo, undefined, "hen.nakee_iv"],
                ] as const).map(([lippu, paalla, muutostieto, mika]) => (
                  <td key={lippu} style={{ textAlign: "center" }}
                      className={odottava(l, lippu) ? "odottaa-vahvistusta" : undefined}>
                    {onPaakayttaja && !poistettu ? (
                      /* ⛔ Ruksi PYSYY labelin sisällä: kävelyn väite klikkaa
                         `closest('label')`, ja se on se polku joka paljastaa
                         väärään riviin osuvan klikkauksen. Teksti on
                         ruudunlukijalle, ei silmälle — paljas ruksi taulukon
                         solussa olisi nimetön myös avustavalle tekniikalle. */
                      <label title={muutostieto
                        ? tr("hen.peruste_title", { peruste: `${perusteenNimi(muutostieto.peruste_laji)}${muutostieto.peruste ? ` — ${muutostieto.peruste}` : ""}` })
                        : tr(mika)}>
                        <input type="checkbox"
                               checked={odottava(l, lippu)?.paalle ?? paalla}
                               disabled={tallennetaan}
                               /* 22.9.2026 tässä oli kaksi vartijaa (onClick + onChange), jotka
                                  torjuivat toisen muutoksen. 23.9. päätös salli useamman: jokainen
                                  klikkaus on nettomuutos tallennettuun eikä mitään torjuta, joten
                                  ruksi on hallittu yhdestä tilasta. */
                               onChange={(e) => kaanna(l, lippu, paalla, e.target.checked)} />
                        <span className="vain-luettava">{l.nimi} — {lippu === "hitsaustyo" ? tr("hen.hitsaustyo") : tr("hen.laiteasennus")}</span>
                      </label>
                    ) : paalla ? (
                      <span title={tr(mika)}>✓</span>
                    ) : <span className="dim">–</span>}
                    {/* ⛔ ODOTTAVA EI SAA NÄYTTÄÄ TALLENNETULTA (Toni 22.9.2026).
                        Ruksi kääntyy heti, koska muuten klikkaus tuntuu menevän
                        hukkaan — mutta silloin se näyttää samalta kuin tallennettu
                        arvo. Ero kerrotaan SANALLA eikä värillä: `--ok` ja
                        `--varoitus` eivät eroa luminanssiltaan (tyylit.css), eikä
                        tätä saa lukea vain väristä. */}
                    {odottava(l, lippu) && (
                      <span className="odottaa-sana">{tr("hen.odottaa_vahvistusta")}</span>
                    )}
                  </td>
                ))}
                {onPaakayttaja && (
                  <td style={{ textAlign: "right" }}>
                    {poistettu ? (
                      <button className="nappi nappi--kevyt" disabled={tallennetaan}
                              onClick={() => void palauta(l)}>{tr("hen.palauta")}</button>
                    ) : (
                      <button className="nappi nappi--kevyt" disabled={tallennetaan}
                              title={l.henkiloita > 0
                                ? tr("hen.poista_nimike_title", { n: l.henkiloita })
                                : tr("hen.poista_nimike_title0")}
                              onClick={() => void passivoi(l)}>{tr("liite.poista")}</button>
                    )}
                  </td>
                )}
              </tr>
              {/* ⛔ PANEELI AVAUTUU MUUTETUN RIVIN ALLE (Toni 22.9.2026).
                  Ennen tätä se oli koko taulukon alapuolella. Seitsemän rivin
                  listalla se jää jo ruudun ulkopuolelle: käyttäjä näkee että
                  ruksi muuttui, muttei sitä että vahvistus odottaa — ja juuri
                  se sekaannus vei Markulta neljä oikeaa lippua 15.9. Syy ja
                  seuraus kuuluvat samaan katseeseen. */}
              {odottavat.length > 0 && viimeisin === l.id && (
                <tr className="rivin-alla" data-odottavat={odottavat.length}>
                  <td colSpan={onPaakayttaja ? 5 : 4}>
                    <form className="allokointi"
                          onSubmit={(e) => { e.preventDefault(); void tallennaMuutokset(); }}>
                      <p style={{ margin: 0, flexBasis: "100%" }}>
                        <b>{odottavat.length === 1 ? tr("hen.muutos_odottaa1") : tr("hen.muutos_odottaa", { n: odottavat.length })}</b>:{" "}
                        {odottavat.map((m) => `${m.l.nimi} — ${m.lippu === "hitsaustyo" ? tr("hen.hitsaustyo") : tr("hen.laiteasennus")} ${m.paalle ? tr("hen.paalle") : tr("hen.pois")}`).join(" · ")}.
                        {" "}{tr("hen.myonto_selite")}
                      </p>
                      {ryhmat.map((r) => (
                        <label key={r}>
                          {ryhmat.length > 1 ? tr("hen.peruste_ryhma", { ryhma: ryhmanOtsikko(r) }) : tr("hen.peruste")}
                          <select value={perusteet[r] ?? ""} data-peruste={r}
                                  onChange={(e) => setPerusteet((p) => ({ ...p, [r]: e.target.value }))} required>
                            <option value="">{tr("hl.valitse")}</option>
                            {perusteLista(r).map((p) => <option key={p.laji} value={p.laji}>{p.nimi}</option>)}
                          </select>
                        </label>
                      ))}
                      <label className="kasvaa">
                        {tr("hen.tarkennus")}{ryhmat.some((r) => perusteet[r] === "muu") ? tr("hen.pakollinen") : ""}
                        <input value={perusteTeksti} data-tarkennus="" onChange={(e) => setPerusteTeksti(e.target.value)} maxLength={500} />
                      </label>
                      <button type="submit" className="nappi nappi--paa" data-tallenna-muutokset=""
                              disabled={tallennetaan || !perusteValmis}>
                        {tallennetaan ? tr("pu.tallennetaan") : tr("hen.tallenna_muutokset")}
                      </button>
                      <button type="button" className="nappi" onClick={hylkaa}>{tr("hen.hylkaa_muutokset")}</button>
                    </form>
                  </td>
                </tr>
              )}
            </Fragment>
            );
          })}
          {luokat.length === 0 && (
            <tr><td colSpan={onPaakayttaja ? 5 : 4} className="dim">{tr("hen.ei_nimikkeita")}</td></tr>
          )}
        </tbody>
      </table>
      </div>
      {luokat.some((l) => l.aktiivinen === false) && (
        <p className="hint" style={{ margin: "4px 12px 8px" }}>
          {tr("hen.passivoitu_selite")}
        </p>
      )}

      {saaMuokata && (
        <form className="allokointi" onSubmit={lisaa}>
          <label className="kasvaa">
            {tr("hen.uusi_nimike")}
            <input value={nimi} onChange={(e) => setNimi(e.target.value)}
                   placeholder={tr("hen.uusi_nimike_esim")} maxLength={100} />
          </label>
          <button type="submit" className="nappi nappi--paa" disabled={tallennetaan || !nimi.trim()}>
            {tallennetaan ? tr("pu.tallennetaan") : tr("hen.lisaa")}
          </button>
          {!tallennetaan && !nimi.trim() && <span className="este-syy" data-este-syy="">{tr("hen.syy_nimike")}</span>}
        </form>
      )}

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
    </section>
  );
}

/* ── yksi henkilö ───────────────────────────────────────── */

function HenkiloRivi({
  henkilo, luokat, tesit, patevyystyypit, saaMuokata, paakayttaja, saaNollataPin, saaArkaluonteiset, saaNahdaPatevyydet, saaMuokataPatevyyksia, auki, avaa, paivita,
}: {
  henkilo: Resurssi; luokat: Osaamisluokka[]; tesit: TesListaus[];
  patevyystyypit: Patevyystyyppi[] | null; saaMuokata: boolean; paakayttaja: boolean;
  /** Toni 10.10.2026: työnjohtaja nollaa omien kohteidensa kenttätilien PIN:n (palvelin tarkistaa kohteen). */
  saaNollataPin?: boolean;
  /** 067: palkka, TES ja matkat vain oikeudella. Ilman sitä rajapinta antaa ne nullina —
   *  ja kortti, joka näytti ja lähetti ne, pyyhki ne tallennettaessa (24.9.2026). */
  saaArkaluonteiset: boolean;
  /** 246 (Toni 16.10.2026): pätevyyspaneelin luku- ja kirjausehto, eri kuin henkilön muokkaus. */
  saaNahdaPatevyydet: boolean; saaMuokataPatevyyksia: boolean;
  auki: boolean; avaa: () => void; paivita: () => Promise<void>;
}) {
  const [paiva, setPaiva] = useState(String(henkilo.tunnit_per_paiva));
  const [tuntipalkka, setTuntipalkka] = useState(
    henkilo.tuntipalkka === null ? "" : String(henkilo.tuntipalkka)
  );
  const [tesId, setTesId] = useState<number | "">(henkilo.tes_id ?? "");
  const [matkatKk, setMatkatKk] = useState(
    henkilo.matkat_per_kk === null ? "" : String(henkilo.matkat_per_kk)
  );
  const [tyotunnus, setTyotunnus] = useState(henkilo.tyotunnus ?? "");
  // Rivi 51: työsuhteen alku ja loppu kirjattavissa — ilman syötettä sulkumekanismi ei laukea koskaan.
  const [aloitti, setAloitti] = useState(henkilo.aloitti ?? "");
  const [paatti, setPaatti] = useState(henkilo.paatti ?? "");
  const [maxPaiva, setMaxPaiva] = useState(String(henkilo.maksimi_per_paiva));
  const [maxViikko, setMaxViikko] = useState(
    henkilo.maksimi_per_viikko === null ? "" : String(henkilo.maksimi_per_viikko)
  );
  const [maxKuukausi, setMaxKuukausi] = useState(
    henkilo.maksimi_per_kuukausi === null ? "" : String(henkilo.maksimi_per_kuukausi)
  );
  const [valitut, setValitut] = useState<number[]>(henkilo.osaamisluokka_idt);
  const [tyopaivat, setTyopaivat] = useState<number[]>(henkilo.tyopaivat);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  useEffect(() => {
    setPaiva(String(henkilo.tunnit_per_paiva));
    setMaxPaiva(String(henkilo.maksimi_per_paiva));
    setMaxViikko(henkilo.maksimi_per_viikko === null ? "" : String(henkilo.maksimi_per_viikko));
    setMaxKuukausi(henkilo.maksimi_per_kuukausi === null ? "" : String(henkilo.maksimi_per_kuukausi));
    setValitut(henkilo.osaamisluokka_idt);
    setTyopaivat(henkilo.tyopaivat);
    setTuntipalkka(henkilo.tuntipalkka === null ? "" : String(henkilo.tuntipalkka));
    setAloitti(henkilo.aloitti ?? ""); setPaatti(henkilo.paatti ?? "");
    setMatkatKk(henkilo.matkat_per_kk === null ? "" : String(henkilo.matkat_per_kk));
    setTesId(henkilo.tes_id ?? "");
    setTyotunnus(henkilo.tyotunnus ?? "");
  }, [henkilo]);

  const numero = (s: string) => Number(s.replace(",", "."));

  async function tallenna() {
    const p = numero(paiva), mp = numero(maxPaiva);
    const mv = maxViikko.trim() === "" ? null : numero(maxViikko);

    if (!(p > 0 && p <= 24)) { setIlmoitus(tr("hen.v_tyopaiva")); return; }
    if (!(mp > 0 && mp <= 24)) { setIlmoitus(tr("hen.v_paivamax")); return; }
    if (mp < p) { setIlmoitus(tr("hen.v_paivamax_pieni")); return; }
    if (mv !== null && !(mv > 0 && mv <= 168)) { setIlmoitus(tr("hen.v_viikkomax")); return; }
    const mk = maxKuukausi.trim() === "" ? null : numero(maxKuukausi);
    if (mk !== null && !(mk > 0 && mk <= 744)) { setIlmoitus(tr("hen.v_kkmax")); return; }
    if (tyopaivat.length === 0) { setIlmoitus(tr("hen.v_tyopaivat")); return; }
    const palkka = tuntipalkka.trim() === "" ? null : numero(tuntipalkka);
    if (palkka !== null && !(palkka >= 0 && palkka <= 9999)) { setIlmoitus(tr("hen.v_palkka")); return; }
    const matkat = matkatKk.trim() === "" ? null : numero(matkatKk);
    if (matkat !== null && !(matkat >= 0 && matkat <= 99)) { setIlmoitus(tr("hen.v_matkat")); return; }

    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.muokkaaHenkiloa(henkilo.id, {
        tunnit_per_paiva: p, maksimi_per_paiva: mp,
        maksimi_per_viikko: mv, maksimi_per_kuukausi: mk,
        tyopaivat: [...tyopaivat].sort((a, b) => a - b),
        // Vain oikeudella: ilman sitä kentissä on piilotettu null, ei «ei asetettu».
        ...(saaArkaluonteiset ? { tuntipalkka: palkka, tes_id: tesId === "" ? null : tesId, matkat_per_kk: matkat } : {}),
        tyotunnus: tyotunnus.trim() || null,
        aloitti: aloitti || null, paatti: paatti || null,
      });
      await api.asetaOsaamiset(henkilo.id, [...new Set(valitut)]);
      setIlmoitus(tr("hen.tallennettu"));
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  const vaihda = (id: number) =>
    setValitut((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...new Set([...v, id])]));

  return (
    <>
      <tr className={auki ? "tr--auki" : ""} data-henkilo={henkilo.id}>
        <th className="th--nimi">
          <span className="lyh">{henkilo.lyhenne ?? "—"}</span>
          {/* T530 (12.10.2026): tunnuksen numero lyhenteen perään, kuten raporteissa «JSa · #135». */}
          {(henkilo.tunnusnumerot ?? []).length > 0 && (
            <span className="tunnusnumero" data-henkilon-tunnus={(henkilo.tunnusnumerot ?? []).join(",")}>
              {" · "}{(henkilo.tunnusnumerot ?? []).map((n) => `#${n}`).join(" ")}
            </span>
          )}
          <span className="kokonimi">
              {/* Sukunimi ensin, koska lista on lajiteltu sukunimen mukaan
                  (API: ORDER BY nimeamaton, sukunimi, etunimi). Jarjestys oli
                  oikein mutta nakymaton: rivi ei alkanut lajitteluavaimesta. */}
              {henkilo.nimeamaton
                ? nimeamattomanSana(henkilo.tyosuhde)
                : `${henkilo.sukunimi}, ${henkilo.etunimi}`}
            </span>
          {(henkilo.puhelin || henkilo.sahkoposti) && (
            <span className="dim" style={{ display: "block", fontSize: "11px", fontWeight: 400 }}>
              {[henkilo.puhelin, henkilo.sahkoposti].filter(Boolean).join(" · ")}
            </span>
          )}
          {/* PÄÄTTYNYT TYÖSUHDE NÄKYY RIVILLÄ, EI SUODATU POIS.
              `f_tyoaika_paiva` on suodattanut `paatti`lla 097:stä
              asti — kenttä on siis ohjannut kapasiteettia, eikä
              yksikään näkymä ole näyttänyt sitä. Mitattu 2.9.2026:
              kolme päättynyttä demossa, eikä niitä voinut nähdä
              mistään; vaikutus näkyi vain tuntisummassa.

              Rivi jää listalle merkittynä eikä katoa: kadonnut
              ihminen on ihmettelyn aihe, ja rekisteri on
              kumulatiivinen tarkoituksella. Sama peruste kuin
              holdatun kohteen allokoinnilla (088). */}
          {henkilo.paatti && (
            <span style={{ display: "inline-block", marginTop: 3, padding: "1px 6px",
                           fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                           color: "var(--himmea)", border: "2px solid var(--himmea)" }}>
              {tr("hen.tyosuhde_paattynyt", { pvm: new Date(henkilo.paatti).toLocaleDateString(lokaali()) })}
              {new Date(henkilo.paatti) < new Date(new Date().toDateString()) && tr("hen.kenttakirjautuminen_estetty")}
            </span>
          )}
        </th>
        <td className="td--vasen">
          {henkilo.osaamiset.length
            ? henkilo.osaamiset.map((o) => nayttonimi("osaamisluokka_nimi", o)).join(" · ")
            : <span className="dim">{tr("hen.ei_osaamisia")}</span>}
        </td>
        <td className="mono">{paivatTeksti(henkilo.tyopaivat)}</td>
        <td className="mono">{luku(henkilo.tunnit_per_paiva)}</td>
        <td className="mono">{luku(henkilo.maksimi_per_paiva)}</td>
        <td className="mono">
          {henkilo.maksimi_per_viikko === null
            ? <span className="dim" title={tr("hen.ei_viikkokattoa_title")}>{tr("hen.ei_kattoa")}</span>
            : luku(henkilo.maksimi_per_viikko)}
        </td>
        <td className="mono">
          {henkilo.maksimi_per_kuukausi === null
            ? <span className="dim" title={tr("hen.ei_kkkattoa_title")}>{tr("hen.ei_kattoa")}</span>
            : luku(henkilo.maksimi_per_kuukausi)}
        </td>
        <td className="td--oikea">
          {saaMuokata && (
            <button className="nappi nappi--kevyt" onClick={avaa} aria-expanded={auki}>
              {auki ? tr("hl.sulje") : tr("qr.muokkaa")}
            </button>
          )}
        </td>
      </tr>

      {auki && saaMuokata && (
        <tr className="tr--laajennus">
          {/* Vasemmalle kuten kentät (24.9.2026): taulukon solut keskitetään, ja kortin otsikot ja ohjeet perivät sen. */}
          <td colSpan={8} className="td--kortti" data-henkilokortti="">
            <div className="laajennus">
              <h3>{tr("hen.osaamiset")}</h3>
              <div className="osaamiset">
                {/* Passivoitu nimike ei ole valittavissa (mitattu 11.9.: Harri Oikari sai
                    passivoidun Projektipäällikön, koska lomake tarjosi sen) — mutta jo
                    valittu jää näkyviin, jotta sen voi poistaa tietoisesti. */}
                {luokat.filter((l) => l.aktiivinen !== false || valitut.includes(l.id)).map((l) => (
                  <label key={l.id} className={"valinta" + (valitut.includes(l.id) ? " valinta--on" : "")}>
                    <input type="checkbox" checked={valitut.includes(l.id)}
                           onChange={() => vaihda(l.id)} />
                    {l.nimi}
                  </label>
                ))}
              </div>

              <h3>{tr("hen.tyopaivat")}</h3>
              <div className="osaamiset">
                {PAIVAT.map((p) => (
                  <label key={p.nro}
                         className={"valinta" + (tyopaivat.includes(p.nro) ? " valinta--on" : "")}>
                    <input type="checkbox" checked={tyopaivat.includes(p.nro)}
                           onChange={() => setTyopaivat((v) =>
                             v.includes(p.nro) ? v.filter((x) => x !== p.nro) : [...v, p.nro])} />
                    {p.lyhenne}
                  </label>
                ))}
              </div>

              <h3>{tr("hen.tyosuhde")}</h3>
              <div className="allokointi">
                <label>
                  {tr("hen.tyotunnus")}
                  <input type="text" size={8} value={tyotunnus} placeholder={tr("hen.tyotunnus_esim")}
                         onChange={(e) => setTyotunnus(e.target.value)} />
                </label>
                <label>
                  {tr("hen.alkoi")}
                  <input type="date" value={aloitti} onChange={(e) => setAloitti(e.target.value)} />
                </label>
                <label title={tr("hen.paattyi_title")}>
                  {tr("hen.paattyi")}
                  <input type="date" value={paatti} onChange={(e) => setPaatti(e.target.value)} data-paatti="" />
                </label>
                {saaArkaluonteiset && (<label>
                  {tr("hen.yleispalkka")}
                  <input type="text" inputMode="decimal" size={12} value={tuntipalkka}
                         placeholder={tr("hen.ei_asetettu")}
                         onChange={(e) => setTuntipalkka(e.target.value)} />
                </label>)}
                {!saaArkaluonteiset ? (
                  <span className="dim" data-arkaluonteiset="piilossa">
                    {tr("hen.arkaluonteiset_piilossa")}
                  </span>
                ) : tesit.length > 0 ? (
                  <label>
                    {tr("hen.tes")}
                    <select value={tesId}
                            onChange={(e) => setTesId(e.target.value ? Number(e.target.value) : "")}>
                      <option value="">{tr("hen.ei_valittu")}</option>
                      {tesit.map((t) => <option key={t.id} value={t.id}>{t.nimi}</option>)}
                    </select>
                  </label>
                ) : (
                  <span className="dim">{tr("hen.tes_lista")}</span>
                )}
                {saaArkaluonteiset && (<label>
                  {tr("hen.matkat_kk")}
                  <input type="text" inputMode="decimal" size={21} value={matkatKk}
                         placeholder={tr("hen.osaamisluokan_mukaan")}
                         onChange={(e) => setMatkatKk(e.target.value)} />
                </label>)}
              </div>
              {saaArkaluonteiset && valitut.length > 0 && (
                <>
                  <h4 className="tyosuhde__alaotsikko">{tr("hen.tehtavapalkat")}</h4>
                  <div className="allokointi">
                    {valitut.map((oid) => {
                      const l = luokat.find((x) => x.id === oid);
                      if (!l) return null;
                      return (
                        <label key={oid}>
                          {l.nimi} €/h
                          <input type="text" inputMode="decimal" size={12}
                                 defaultValue={henkilo.palkat[String(oid)] ?? ""}
                                 placeholder={tr("hen.yleispalkka_pieni")}
                                 onBlur={(e) => {
                                   const v = e.target.value.trim();
                                   void api.asetaTehtavapalkka(henkilo.id, oid,
                                     v === "" ? null : Number(v.replace(",", ".")));
                                 }} />
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <p className="ikkuna__vihje">
                {tr("hen.palkka_vihje")}
              </p>

              <h3>{tr("hen.tyoaika")}</h3>
              <div className="allokointi">
                <label>
                  {tr("hen.tyopaiva_h")}
                  <input type="text" inputMode="decimal" size={4} value={paiva}
                         onChange={(e) => setPaiva(e.target.value)} />
                </label>
                <label>
                  {tr("hen.max_pv")}
                  <input type="text" inputMode="decimal" size={4} value={maxPaiva}
                         onChange={(e) => setMaxPaiva(e.target.value)} />
                </label>
                <label>
                  {tr("hen.max_vko")}
                  <input type="text" inputMode="decimal" size={10} value={maxViikko}
                         placeholder={tr("hen.ei_kattoa")}
                         onChange={(e) => setMaxViikko(e.target.value)} />
                </label>
                <label>
                  {tr("hen.max_kk")}
                  <input type="text" inputMode="decimal" size={10} value={maxKuukausi}
                         placeholder={tr("hen.ei_kattoa")}
                         onChange={(e) => setMaxKuukausi(e.target.value)} />
                </label>
              </div>

              {/* Tallenna omalle rivilleen, ei työaikarivin perään (Toni 18.9.2026:
                  «näyttää kuin kuuluisi VAIN Työaikaan»). Yksi nappi tallentaa koko
                  kortin — osaamiset, työpäivät, työsuhteen ja työajan — ja sen on
                  näytettävä siltä. Sijainti oli mittaamaton oletus: nappi oli siinä
                  divissä johon se sattui syntymään. */}
              <div className="lomake__toiminnot"
                   style={{ display: "flex", alignItems: "center", gap: 12,
                            borderTop: "1px solid var(--viiva)", paddingTop: 10, marginTop: 4 }}>
                <button className="nappi nappi--paa" disabled={tallennetaan}
                        onClick={() => void tallenna()}>
                  {tallennetaan ? tr("pu.tallennetaan") : tr("hen.tallenna_tiedot")}
                </button>
                <span className="dim">
                  {tr("hen.tallenna_selite")}
                </span>
              </div>

              {/* 106: tunnukset johdetaan ihmisestä. Tämä on ainoa paikka josta
                  ihmisen tunnus luodaan — Käyttäjät-näkymässä ei ole enää
                  "Uusi käyttäjä" -nappia, koska tili ilman ihmistä ei ole
                  mahdollinen tila. Kumpaakaan ei tarvitse olla. */}
              {!henkilo.nimeamaton && (
                <Tunnukset henkilo={henkilo} paakayttaja={paakayttaja} saaNollataPin={saaNollataPin ?? false} paivita={paivita} />
              )}

              {/* Pätevyydet myös henkilön alla: osaamiset ja pätevyydet ovat
                  eri asioita, mutta molemmat kuuluvat henkilöön. Kirjaus ja
                  todistukset onnistuvat täältä; sama data kuin Pätevyydet-
                  näkymässä. Näytetään vain jos pätevyysmoduuli on käytössä. */}
              {patevyystyypit && saaNahdaPatevyydet && (
                <HenkilonPatevyydet
                  henkiloId={henkilo.id} nimi={henkilo.nimi}
                  tyypit={patevyystyypit} saaMuokata={saaMuokataPatevyyksia}
                />
              )}

              {/* Poissaolot henkilön alla: Jussi kysyi missä lomat merkitään.
                  Sama data kuin resursoinnissa; vuokrapaikalle ei lomia. */}
              {!henkilo.nimeamaton && (
                <HenkilonPoissaolot
                  henkiloId={henkilo.id} nimi={henkilo.nimi}
                  saaMuokata={saaMuokata}
                />
              )}

              {/* Työsopimukset, perehdytystodistukset, salassapitositoumukset.
                  Sama komponentti kuin pätevyystodistuksilla — liitejärjestelmä
                  on yleinen, ja kohde annetaan taulun nimenä. */}
              <Liitteet
                kohdeTaulu="henkilo" kohdeId={henkilo.id}
                saaMuokata={saaMuokata}
                otsikko={tr("hen.asiakirjat")}
                arkaluonteinenOletus
              />

              <p className="ikkuna__vihje">
                {tr("hen.tyoaika_vihje")}
              </p>

              {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── uusi henkilö ───────────────────────────────────────── */

/*
 * Ihmisen kaksi tunnusta, molemmat valinnaisia:
 *   kenttätunnus  koodi + PIN   → kenttäkirjaus puhelimella
 *   käyttäjätili  sähköposti    → selain, roolit
 * Näytetään kumpi on ja kumpi ei; pääkäyttäjä luo puuttuvan tästä.
 * PIN ja kutsulinkki näytetään kerran eikä tallenneta mihinkään.
 */
function Tunnukset({
  henkilo, paakayttaja, saaNollataPin, paivita,
}: {
  henkilo: Resurssi; paakayttaja: boolean; saaNollataPin: boolean; paivita: () => Promise<void>;
}) {
  const [lomake, setLomake] = useState<"kayttajatili" | "kenttatunnus" | null>(null);
  // Työmaa on valinnainen (Toni 13.9.2026): sidonta seuraa allokaatiosta, tässä valittu on lisäsidonta.
  // Kohteet haetaan vasta kun lomake avataan.
  const [kohteet, setKohteet] = useState<Kohde[] | null>(null);
  const [tyomaat, setTyomaat] = useState<number[]>([]);
  const [kentanKieli, setKentanKieli] = useState("");
  const [sahkoposti, setSahkoposti] = useState("");
  const [rooli, setRooli] = useState("suunnittelija");
  const [tulos, setTulos] = useState<{ otsikko: string; kohde: string; selite: string; arvo: string; kutsu?: { osoite: string; syy: string | null } } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  async function avaaKentta() {
    setVirhe(null); setLomake("kenttatunnus");
    if (kohteet === null) {
      try { setKohteet(await api.kohteet()); }
      catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.tyomaat_virhe")); }
    }
  }

  async function luoKentta(e: React.FormEvent) {
    e.preventDefault();
    // Työmaa valinnainen (Toni 13.9.2026): ilman sitä tunnus näkee omat tietonsa mutta ei yhtään kohdetta.
    setVirhe(null); setTallennetaan(true);
    try {
      const t = await api.luoKenttatunnus(henkilo.id, tyomaat, kentanKieli || null);
      setTulos({ otsikko: tr("hen.kenttatunnus_ja_pin"), kohde: `${henkilo.nimi} · ${t.koodi}`, selite: "PIN", arvo: t.pin });
      setLomake(null); setTyomaat([]);
      await paivita();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.kenttatunnus_virhe"));
    } finally { setTallennetaan(false); }
  }

  /* Uusi PIN olemassa olevalle kenttätilille (Toni 10.10.2026): pääkäyttäjä ja työnjohtaja. Palvelin rajaa työnjohtajan
     omien kohteiden tileihin ja kieltää tilit, joilla on hallintorooli; vastaus näkyy sanoin. Vahvistus kysytään, koska
     nollaus katkaisee kentällä olevan kirjautumisen. */
  async function uusiPin() {
    if (!window.confirm(tr("hen.uusi_pin_vahvistus1", { nimi: henkilo.nimi }) + "\n\n" + tr("hen.uusi_pin_vahvistus2"))) return;
    setVirhe(null); setTallennetaan(true);
    try {
      const t = await api.uusiPin(henkilo.id);
      setTulos({ otsikko: tr("hen.uusi_pin"), kohde: `${henkilo.nimi} · ${t.koodi}`, selite: "PIN", arvo: t.pin });
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.pin_virhe"));
    } finally { setTallennetaan(false); }
  }

  /* 217 (Toni 10.10.2026 «Tee kaikki 4»): Academy kenttätilille henkilökortilta. Sama ehto kuin PIN-nollauksessa:
     pääkäyttäjä tai kohteen työnjohtaja omille kohteilleen; palvelin päättää ja vastaa sanoin. */
  async function vaihdaAcademy() {
    const uusi = !henkilo.academy_on;
    setVirhe(null); setTallennetaan(true);
    try { await api.asetaAcademy(henkilo.id, uusi); await paivita(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.academy_virhe")); }
    finally { setTallennetaan(false); }
  }

  async function luoTili(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null); setTallennetaan(true);
    try {
      const t = await api.luoKayttajatili(henkilo.id, { sahkoposti: sahkoposti.trim(), roolit: [rooli] });
      setTulos({ otsikko: tr("hen.kutsulinkki"), kohde: henkilo.nimi, selite: tr("hen.linkki"), arvo: t.kutsu, kutsu: { osoite: sahkoposti.trim(), syy: null } });
      setLomake(null); setSahkoposti("");
      await paivita();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.kayttajatili_virhe"));
    } finally { setTallennetaan(false); }
  }

  // Sana ja väri: ON/EI luettavissa mustavalkoisena (paletin sääntö).
  const tila = (on: boolean) => (
    <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                   padding: "1px 6px", border: "1px solid " + (on ? TILAVARI.kunnossa : "var(--viiva)"),
                   color: on ? TILAVARI.kunnossa : "var(--himmea)", marginLeft: 6 }}>
      {on ? tr("hen.on") : tr("hen.ei")}
    </span>
  );

  return (
    <>
      <h3>{tr("hen.tunnukset")}</h3>
      <div className="allokointi" style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span>{tr("hen.kenttatunnus")} {tila(henkilo.kenttatunnus_on)}</span>
        {paakayttaja && !henkilo.kenttatunnus_on && lomake === null && (
          <button type="button" className="nappi nappi--kevyt" disabled={tallennetaan} onClick={() => void avaaKentta()}>
            {tr("hen.luo_kenttatunnus")}
          </button>
        )}
        {saaNollataPin && henkilo.kenttatunnus_on && lomake === null && (
          <button type="button" className="nappi nappi--kevyt" disabled={tallennetaan} data-uusi-pin={henkilo.id} onClick={() => void uusiPin()}>
            {tr("hen.anna_uusi_pin")}
          </button>
        )}
        {saaNollataPin && henkilo.kenttatunnus_on && lomake === null && henkilo.academy_on != null && (
          <span>
            {tr("hen.academy")} {tila(!!henkilo.academy_on)}
            <button type="button" className="nappi nappi--kevyt" style={{ marginLeft: 8 }} disabled={tallennetaan}
                    data-academy-kytkin={henkilo.id} data-academy-on={henkilo.academy_on ? "1" : "0"} onClick={() => void vaihdaAcademy()}>
              {henkilo.academy_on ? tr("hen.academy_pois") : tr("hen.academy_anna")}
            </button>
          </span>
        )}
        <span>{tr("hen.kayttajatili")} {tila(henkilo.kayttajatili_on)}</span>
        {paakayttaja && !henkilo.kayttajatili_on && lomake === null && (
          <button type="button" className="nappi nappi--kevyt" disabled={tallennetaan} onClick={() => setLomake("kayttajatili")}>
            {tr("hen.luo_kayttajatili")}
          </button>
        )}
      </div>
      {lomake === "kenttatunnus" && (
        <form className="allokointi" style={{ alignItems: "flex-end", gap: 8, flexWrap: "wrap" }} onSubmit={luoKentta}>
          {kohteet === null
            ? <span className="dim">{tr("hen.ladataan_tyomaita")}</span>
            : <TyomaaValinta kohteet={kohteet} valitut={tyomaat} muuta={setTyomaat} />}
          <TunnuksenKieli arvo={kentanKieli} muuta={setKentanKieli} merkinta="henkilokortti" />
          <button type="submit" className="nappi nappi--paa" disabled={tallennetaan}>
            {tallennetaan ? tr("hen.luodaan") : tyomaat.length === 0 ? tr("hen.luo_ilman_tyomaata") : tr("hen.luo_kentta_ja_pin")}
          </button>
          <button type="button" className="nappi nappi--kevyt" onClick={() => { setLomake(null); setTyomaat([]); }}>{tr("hl.peruuta")}</button>
        </form>
      )}
      {lomake === "kayttajatili" && (
        <form className="allokointi" style={{ alignItems: "flex-end", gap: 8, flexWrap: "wrap" }} onSubmit={luoTili}>
          <label>
            {tr("kirjautuminen.sahkoposti")}
            <input type="email" value={sahkoposti} required autoFocus autoComplete="off"
                   onChange={(e) => setSahkoposti(e.target.value)} />
          </label>
          <label>
            {tr("hen.rooli")}
            <select value={rooli} onChange={(e) => setRooli(e.target.value)}>
              <option value="suunnittelija">{nayttonimi("rooli", "suunnittelija")}</option>
              <option value="tyonjohtaja">{nayttonimi("rooli", "tyonjohtaja")}</option>
              <option value="lukija">{nayttonimi("rooli", "lukija")}</option>
              <option value="paakayttaja">{nayttonimi("rooli", "paakayttaja")}</option>
            </select>
          </label>
          <button type="submit" className="nappi nappi--paa" disabled={tallennetaan}>
            {tallennetaan ? tr("hen.luodaan") : tr("hen.luo_ja_kutsu")}
          </button>
          <button type="button" className="nappi nappi--kevyt" onClick={() => setLomake(null)}>{tr("hl.peruuta")}</button>
        </form>
      )}
      {tulos && (
        <Salaisuus otsikko={tulos.otsikko} kohde={tulos.kohde} rivit={[{ selite: tulos.selite, arvo: tulos.arvo }]}
          vihje={tulos.kutsu ? tr("hen.kutsu_vihje") : tr("hen.pin_vihje")}
          lahetys={tulos.kutsu ? {
            osoite: tulos.kutsu.osoite, syy: tulos.kutsu.syy,
            laheta: async () => {
              const t = await api.luoKutsuHenkilolle(henkilo.id, true);
              setTulos((v) => (v ? { ...v, arvo: t.kutsu } : v));
              return t.lahetys.lahetetty
                ? { ok: true, viesti: tr("hen.linkki_sama") }
                : { ok: false, viesti: tr("hen.ei_lahetetty", { syy: t.lahetys.syy ?? tr("hen.tuntematon_syy") }) };
            },
          } : undefined}
          sulje={() => setTulos(null)} />
      )}
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
    </>
  );
}

function UusiHenkilo({
  luokat, peruuta, valmis,
}: {
  luokat: Osaamisluokka[]; peruuta: () => void; valmis: () => Promise<void>;
}) {
  const [etunimi, setEtunimi] = useState("");
  const [sukunimi, setSukunimi] = useState("");
  const [nimike, setNimike] = useState("");
  const [lyhenneSyote, setLyhenneSyote] = useState("");
  const [tyosuhde, setTyosuhde] = useState<"oma" | "vuokra" | "alihankinta">("oma");
  const [nimeamaton, setNimeamaton] = useState(false);
  const [valitut, setValitut] = useState<number[]>([]);
  const [tyopaivat, setTyopaivat] = useState<number[]>([1, 2, 3, 4, 5]);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  const vaihda = (id: number) =>
    setValitut((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...new Set([...v, id])]));

  async function tallenna(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null);
    setTallennetaan(true);
    try {
      const t = await api.luoHenkilo({
        etunimi: etunimi.trim(), sukunimi: sukunimi.trim(),
        ...(nimike.trim() && { nimike: nimike.trim() }),
        ...(lyhenneSyote.trim() && { lyhenne: lyhenneSyote.trim() }),
        tyosuhde, nimeamaton,
        tyopaivat: [...tyopaivat].sort((a, b) => a - b),
        ...(valitut.length && { osaamisluokka_idt: valitut }),
      });
      setVirhe(null);
      await valmis();
      // Lyhenne kerrotaan, koska se generoitiin eikä käyttäjä valinnut sitä.
      alert(tr("hen.lisatty", { lyh: String(t.lyhenne) }));
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
      setTallennetaan(false);
    }
  }

  return (
    <section className="panel lomake">
      <div className="panel__head">
        <h2>{tr("hen.uusi_henkilo")}</h2>
        <span className="hint">{tr("hen.lyhenne_vihje")}</span>
      </div>
      <form className="lomake__kentat" onSubmit={tallenna}>
        <label className="kasvaa">
          {tr("hen.etunimi")}
          <input value={etunimi} onChange={(e) => setEtunimi(e.target.value)} required autoFocus />
        </label>
        <label className="kasvaa">
          {tr("hen.sukunimi")}
          <input value={sukunimi} onChange={(e) => setSukunimi(e.target.value)} required />
        </label>
        <label className="kasvaa">
          {tr("hen.nimike")}
          <input value={nimike} onChange={(e) => setNimike(e.target.value)} placeholder={tr("hen.nimike_esim")} />
        </label>
        <label>
          {tr("hen.lyhenne")}
          <input value={lyhenneSyote} maxLength={8} size={10} placeholder={tr("hen.johdetaan")}
                 title={tr("hen.lyhenne_title")}
                 onChange={(e) => setLyhenneSyote(e.target.value)} />
        </label>
        <label>
          {tr("hen.tyosuhde")}
          <select value={tyosuhde} onChange={(e) => {
            const v = e.target.value as "oma" | "vuokra" | "alihankinta";
            setTyosuhde(v);
            if (v === "oma") setNimeamaton(false);
          }}>
            <option value="oma">{tr("hen.tyosuhde_oma")}</option>
            <option value="vuokra">{tr("hen.tyosuhde_vuokra")}</option>
            <option value="alihankinta">{tr("hen.tyosuhde_alihankinta")}</option>
          </select>
        </label>

        {tyosuhde !== "oma" && (
          <label className="valinta valinta--leveä">
            <input type="checkbox" checked={nimeamaton}
                   onChange={(e) => setNimeamaton(e.target.checked)} />
            {tr("hen.nimeamaton")}
          </label>
        )}

        <div className="lomake__osaamiset">
          <span className="lomake__otsikko">{tr("hen.tyopaivat")}</span>
          <div className="osaamiset">
            {PAIVAT.map((p) => (
              <label key={p.nro}
                     className={"valinta" + (tyopaivat.includes(p.nro) ? " valinta--on" : "")}>
                <input type="checkbox" checked={tyopaivat.includes(p.nro)}
                       onChange={() => setTyopaivat((v) =>
                         v.includes(p.nro) ? v.filter((x) => x !== p.nro) : [...v, p.nro])} />
                {p.lyhenne}
              </label>
            ))}
          </div>
        </div>

        <div className="lomake__osaamiset">
          <span className="lomake__otsikko">{tr("hen.osaamiset")}</span>
          <div className="osaamiset">
            {luokat.filter((l) => l.aktiivinen !== false).map((l) => (
              <label key={l.id} className={"valinta" + (valitut.includes(l.id) ? " valinta--on" : "")}>
                <input type="checkbox" checked={valitut.includes(l.id)}
                       onChange={() => vaihda(l.id)} />
                {l.nimi}
              </label>
            ))}
          </div>
        </div>

        <div className="lomake__toiminnot">
          <button type="submit" className="nappi nappi--paa" disabled={tallennetaan}>
            {tallennetaan ? tr("pu.tallennetaan") : tr("ltar.tallenna")}
          </button>
          <button type="button" className="nappi nappi--kevyt" onClick={peruuta}>{tr("hl.peruuta")}</button>
        </div>

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      </form>
    </section>
  );
}



/** ISO-viikonpäivät: 1 = ma … 7 = su. */
const PAIVAT: { nro: number; lyhenne: string }[] = [
  { nro: 1, get lyhenne() { return tr("hen.pv.ma"); } }, { nro: 2, get lyhenne() { return tr("hen.pv.ti"); } },
  { nro: 3, get lyhenne() { return tr("hen.pv.ke"); } }, { nro: 4, get lyhenne() { return tr("hen.pv.to"); } },
  { nro: 5, get lyhenne() { return tr("hen.pv.pe"); } }, { nro: 6, get lyhenne() { return tr("hen.pv.la"); } },
  { nro: 7, get lyhenne() { return tr("hen.pv.su"); } },
];

const paivatTeksti = (t: number[]) =>
  t.length === 0 ? "—"
  : PAIVAT.filter((p) => t.includes(p.nro)).map((p) => p.lyhenne).join(" ");
